/**
 * Issue Credit Modal Component
 * Modal for issuing credits to a lease
 */

import { useState } from 'react';
import { type CreateCreditInput } from '../../lib/api';
import { todayInTimezone } from '@resida/shared';
import { usePropertyTimezone } from '../../hooks/usePropertyTimezone';

interface IssueCreditModalProps {
  onSave: (data: CreateCreditInput) => Promise<void>;
  onCancel: () => void;
}

export function IssueCreditModal({ onSave, onCancel }: IssueCreditModalProps) {
  const timezone = usePropertyTimezone();
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [useEffectiveDate, setUseEffectiveDate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const data: CreateCreditInput = {
        amount: Math.round(parseFloat(amount) * 100),
        description,
        // effectiveDate is YYYY-MM-DD from the date input — send directly, do not convert via toISOString()
        ...(useEffectiveDate && effectiveDate && { effectiveDate }),
      };

      await onSave(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to issue credit');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      data-testid="issue-credit-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-credit-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="issue-credit-title" className="text-xl font-semibold m-0">
          Issue Credit
        </h3>
        <p className="text-sm text-muted-foreground mt-1 m-0">
          Issue a credit to reduce the tenant's balance
        </p>

        {error && (
          <div
            data-testid="issue-credit-error"
            role="alert"
            aria-live="polite"
            className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 mt-4"
          >
            <p className="text-sm text-destructive m-0">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="issue-credit-form" className="mt-4">
          <div className="grid gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="credit-amount"
                className="text-sm font-medium text-foreground"
              >
                Amount ($) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-base">
                  $
                </span>
                <input
                  id="credit-amount"
                  data-testid="issue-credit-amount-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 pl-7 text-sm border border-input rounded-md min-h-[44px]"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="credit-description"
                className="text-sm font-medium text-foreground"
              >
                Description *
              </label>
              <input
                id="credit-description"
                data-testid="issue-credit-description-input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                aria-required="true"
                placeholder="e.g., Late fee waived"
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              />
            </div>

            <div>
              <div className="flex items-center mb-2">
                <input
                  id="use-effective-date"
                  data-testid="use-effective-date-checkbox"
                  type="checkbox"
                  checked={useEffectiveDate}
                  onChange={(e) => setUseEffectiveDate(e.target.checked)}
                  className="mr-2"
                />
                <label
                  htmlFor="use-effective-date"
                  className="text-sm font-medium cursor-pointer"
                >
                  Schedule for future date
                </label>
              </div>
              {useEffectiveDate && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="effective-date"
                    className="text-sm font-medium text-foreground"
                  >
                    Effective Date *
                  </label>
                  <input
                    id="effective-date"
                    data-testid="issue-credit-effective-date-input"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    required={useEffectiveDate}
                    min={todayInTimezone(timezone)}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Credit will be applied on this date
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              data-testid="issue-credit-cancel"
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="issue-credit-submit"
              disabled={isSaving}
              aria-disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isSaving ? 'Issuing...' : 'Issue Credit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
